import { FC, PropsWithChildren } from 'react';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { BoxV2 as Box, Flex } from 'portal-commons';

import Divider from './Divider';

// Props for the main component
interface RcsSectionBlockProps {
  icon?: FontAwesomeIconProps['icon'];
  title: string;
  showDivider?: boolean;
  'data-testid'?: string;
  sx?: object;
}

// Internal sub-component that combines the old Header and Content
const SectionBody: FC<PropsWithChildren<RcsSectionBlockProps>> = ({
  icon,
  title,
  showDivider,
  children,
  'data-testid': dataTestId,
}) => (
  <>
    {/* Header Logic */}
    <Flex
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        color: 't.black70',
        marginBottom: showDivider ? 's' : 'l',
      }}
      data-testid={dataTestId}
    >
      {icon && (
        <Box sx={{ marginRight: 'xs' }}>
          <FontAwesomeIcon icon={icon} fontSize="24px" />
        </Box>
      )}
      <Box as="h3" sx={{ fontSize: 'H500', fontWeight: 'semiBold' }}>
        {title}
      </Box>
    </Flex>
    {showDivider && <Divider />}
    <Flex
      sx={{ flexDirection: 'column', gap: 'l' }}
      data-testid={`sectionContent-${dataTestId}`}
    >
      <div>{children}</div>
    </Flex>
  </>
);

// Define a composite type for the component and its sub-component
type RcsSectionBlockComponent = FC<RcsSectionBlockProps> & {
  Body: typeof SectionBody;
};

// Main component, now acting as a styled wrapper
const RcsSectionBlock: RcsSectionBlockComponent = ({
  icon,
  title,
  showDivider = true,
  children,
  'data-testid': dataTestId,
  sx,
}) => {
  return (
    <Flex
      as="section"
      sx={{
        flexDirection: 'column',
        pt: 'xl',
        pb: 'xxl',
        paddingX: 'xxl',
        border: '1px solid',
        borderColor: 't.black20',
        backgroundColor: 't.white',
        ...sx,
      }}
      data-testid="rcsSection"
    >
      <SectionBody
        icon={icon}
        title={title}
        showDivider={showDivider}
        data-testid={dataTestId}
      >
        {children}
      </SectionBody>
    </Flex>
  );
};

RcsSectionBlock.Body = SectionBody;

export default RcsSectionBlock;
